import { TenantConfig } from '../../../app.constants';
import { Metadata } from '../../../_objects/inspection-image';
import { ReportLocation } from '../../../_objects/inspection-report';
import { SubjectLocation, SubjectModel } from '../../../_objects/subject-model';
import { PreviewImageDetail } from './preview-image-detail';
import { ImageComponent, TableData } from './TableData';
import { DataProperty } from '../../electron.constants';

export class ReportDataGenerator {
  private subjectModel: SubjectModel;
  private tableData: TableData;
  private tableDataList: TableData[];
  private inspectionReportLocations: ReportLocation[];
  private allInspectionImages: PreviewImageDetail[];
  private tenantId: string;

  constructor(subjectModel: SubjectModel, allInspectionImages: PreviewImageDetail[], tenantId: string) {
    this.subjectModel = subjectModel;
    this.tenantId = tenantId;
    this.allInspectionImages = allInspectionImages;
    this.tableDataList = [];
  }

  public generateReportData(reportLocations: ReportLocation[], subjectModel: SubjectModel, dataProperty: number) {
    // init
    this.inspectionReportLocations = reportLocations;
    const locations = subjectModel.locations;
    locations.forEach(location => {
      if (!location.isDataPlate) {
        this.setReportDataList(location, [location], dataProperty, reportLocations);
      }
    });

    return this.tableDataList;
  }

  public separatePageReportData(target: TableData[], rowMax: number, type: string) {
    const pages: TableData[][] = [];
    let page: TableData[] = [];
    let counter = 0;
    const mirrorHorizontalAndRotate270 = 5;
    switch (type) {
      case 'Summary':
        target.forEach(data => {
          counter++;
          page.push(data);
          if (counter >= rowMax) {
            pages.push(page);
            page = [];
            counter = 0;
          }
        });
        break;
      case 'Picture':
        target.forEach(data => {
          let clone = data.getClone();
          clone.images = [];
          data.images.forEach(image => {
            if (counter === (rowMax - 1) && image.usedImageMode == 0 && image.height >= image.width && image.orientation < mirrorHorizontalAndRotate270) {
              if (clone.images.length > 0) {
                page.push(clone);
              }

              pages.push(page);
              page = [];
              clone = data.getClone();
              clone.images = [];
              counter = 0;
            }

            if (counter === (rowMax - 1) && image.usedImageMode == 0 && image.height < image.width && image.orientation >= mirrorHorizontalAndRotate270) {
              if (clone.images.length > 0) {
                page.push(clone);
              }

              pages.push(page);
              page = [];
              clone = data.getClone();
              clone.images = [];
              counter = 0;
            }

            if (counter === (rowMax - 1) && image.usedImageMode == 1 && image.modifiedHeight > image.modifiedWidth) {
              if (clone.images.length > 0) {
                page.push(clone);
              }

              pages.push(page);
              page = [];
              clone = data.getClone();
              clone.images = [];
              counter = 0;
            }

            // 縦長画像の場合、カウント追加
            if ((image.usedImageMode == 0 && image.height > image.width && image.orientation < mirrorHorizontalAndRotate270)
              || (image.usedImageMode == 0 && image.width > image.height && image.orientation >= mirrorHorizontalAndRotate270)
              || (image.usedImageMode == 1 && image.modifiedHeight > image.modifiedWidth)) {
              counter++;
            }

            if (counter < rowMax) {
              counter++;
              clone.images.push(image);
            }
            if (counter >= rowMax) {
              page.push(clone);
              pages.push(page);
              page = [];
              clone = data.getClone();
              clone.images = [];
              counter = 0;
            }
          });
          if (clone.images.length > 0) {
            page.push(clone);
          }
        });
        break;
      default:
        break;
    }
    // 最終ページ処理
    if (page.length > 0) {
      pages.push(page);
    }

    return pages;
  }

  public generateReportDataPlateImage(reportLocations: ReportLocation[], dataPlateLocations: SubjectLocation[]) {
    const list: ImageComponent[] = [];

    dataPlateLocations.forEach(dpLocation => {
      const location: ReportLocation = reportLocations.find(loc => loc.identifier === dpLocation.identifier);

      if (location === null) {
        return;
      }

      const dataPlateImages = this.allInspectionImages
        .filter(img => location?.imageIds.some(id => img.id === id) && !img.isDummy)
        .sort((a, b) => this.imgSort(a, b));

      dataPlateImages.forEach(img => {
        const component = new ImageComponent(img.id, img.filePath, img.width, img.height, false, img.orientation, img.usedImageMode, img.modifiedWidth, img.modifiedHeight);
        component.caption = img.subjectLocationId;
        list.push(component);
      });
    });

    return list;
  }

  private setReportDataList(location: SubjectLocation, locations: SubjectLocation[], dataProperty: number,
    inspectionReportLocations: ReportLocation[]) {
    if (location.children.length > 0) {
      location.children.forEach(loc => {
        const copyLocations = JSON.parse(JSON.stringify(locations));
        copyLocations.push(loc);
         this.setReportDataList(loc, copyLocations, dataProperty, inspectionReportLocations);
      });
    }

    const inspectionReportLocation = inspectionReportLocations.find(image => image.identifier === location.identifier);
    if (inspectionReportLocation == null) {
      return null;
    }

    // init
    this.tableData = new TableData();

    switch (dataProperty) {
      case DataProperty.WorstOnly:
        this.setTableDataWorstOnly(inspectionReportLocation, locations, location);
        break;
      case DataProperty.All:
        this.setTableData(inspectionReportLocation, locations, location);
        break;
      default:
        break;
    }
    return this.tableData;
  }

  private setTableData(inspectionReportLocation: ReportLocation,
    locations: SubjectLocation[], location: SubjectLocation) {
    // If SortOrder is NULL, the last in ascending order of date and time.
    const inspectionImages = this.allInspectionImages
      .filter(img => inspectionReportLocation.imageIds.some(id => id === img.id))
      .sort((a, b) => this.imgSort(a, b));

    if (!inspectionImages.length) {
      const dummy = new PreviewImageDetail();
      dummy.isDummy = true;
      inspectionImages.push(dummy);
    }

    const doneImageIds: string[] = [];

    inspectionImages.forEach(image => {
      this.tableData = new TableData();

      if (doneImageIds.some(id => id === image.id)) {
        return;
      }

      if (image.isDummy) {
        doneImageIds.push(image.id);
        this.tableData.images.push(new ImageComponent(image.id, image.filePath, image.width,
          image.height, true, image.orientation, image.usedImageMode, image.modifiedWidth, image.modifiedHeight));
      } else {
        doneImageIds.push(image.id);
        // ReportBindRepository側でSAS付URLに変換済
        this.tableData.images.push(new ImageComponent(image.id, image.filePath, image.width,
          image.height, false, image.orientation, image.usedImageMode, image.modifiedWidth, image.modifiedHeight));

        if (image.metadata == null) {
          image.metadata = new Metadata();
        }

        this.tableData.comment = image.comment ?? '';
        this.tableData.mark = image.metadata.mark ?? TenantConfig.DefaultMarkWhenUploadingNoMarkImage.X;
        this.tableData.title = image.metadata.title;
        this.tableData.previewImage = image;
      }

      locations.forEach(loc => {
        this.tableData.identifiers.push(loc.identifier);
        this.tableData.names.push(loc.name);
      });

      this.tableData.id = inspectionReportLocation.identifier;
      this.tableData.inspectionFor = location.inspectionFor;

      this.tableDataList.push(this.tableData);
    });
  }

  private setTableDataWorstOnly(inspectionReportLocation: ReportLocation,
    locations: SubjectLocation[], location: SubjectLocation) {
    // If SortOrder is NULL, the last in ascending order of date and time.
    const inspectionImages = this.allInspectionImages
      .filter(img => inspectionReportLocation.imageIds.some(id => id === img.id))
      .sort((a, b) => this.imgSort(a, b));

    if (!inspectionImages.length) {
      const dummy = new PreviewImageDetail();
      dummy.isDummy = true;
      inspectionImages.push(dummy);
    }

    const doneImageIds: string[] = [];
    inspectionImages.forEach(image => {
      if (doneImageIds.some(id => id === image.id)) {
        return;
      }

      if (image.isDummy) {
        doneImageIds.push(image.id);
        this.tableData.images.push(new ImageComponent(image.id, image.filePath, image.width,
          image.height, true, image.orientation, image.usedImageMode, image.modifiedWidth, image.modifiedHeight));
      } else {
        doneImageIds.push(image.id);
        this.tableData.images.push(new ImageComponent(image.id, image.filePath, image.width,
          image.height, false, image.orientation, image.usedImageMode, image.modifiedWidth, image.modifiedHeight));

        image.comment = image.comment ?? '';
        image.metadata.mark = image.metadata.mark ?? TenantConfig.DefaultMarkWhenUploadingNoMarkImage.X;

        // ファイルマークが現在より悪い場合更新
        if (!this.tableData.mark || image.metadata.mark > this.tableData.mark) {
          this.tableData.mark = image.metadata.mark;
          this.tableData.title = image.metadata.title;
          this.tableData.comment = image.comment;
          this.tableData.previewImage = image;
        } else if (image.metadata.mark === this.tableData.mark
          && image.comment?.length >= this.tableData.comment?.length) {
          // ファイルマークが同じ場合、コメントが長い方に更新
          this.tableData.title = image.metadata.title;
          this.tableData.comment = image.comment;
          this.tableData.previewImage = image;
        }
      }
    });

    locations.forEach(loc => {
      this.tableData.identifiers.push(loc.identifier);
      this.tableData.names.push(loc.name);
    });

    this.tableData.id = inspectionReportLocation.identifier;
    this.tableData.inspectionFor = location.inspectionFor;

    this.tableDataList.push(this.tableData);
  }

  private imgSort(a: PreviewImageDetail, b: PreviewImageDetail) {
    if (a == null && b == null) {
      return 0;
    } else if (a == null) {
      return 1;
    } else if (b == null) {
      return -1;
    }

    if (a.sortOrder == null && b.sortOrder == null) {
      return 0;
    } else if (a.sortOrder == null || a.sortOrder > b.sortOrder) {
      return 1;
    } else if (b.sortOrder == null || a.sortOrder < b.sortOrder) {
      return -1;
    }

    if (a.datetime == null && b.datetime == null) {
      return 0;
    } else if (a.datetime == null) {
      return 1;
    } else if (b.datetime == null) {
      return -1;
    }

    return (a.datetime < b.datetime ? -1 : 1);
  }

}
